import React, { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Shield, ArrowUpRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

export default function LoginPage() {
    const { user, login, loading } = useAuth();
    const nav = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);

    if (loading) return null;
    if (user) return <Navigate to="/admin" replace />;

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await login(email, password);
            toast.success("Welcome back.");
            nav("/admin");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally { setBusy(false); }
    };

    return (
        <div data-testid="login-page" className="min-h-[calc(100vh-5rem)] grid lg:grid-cols-2">
            <div className="hidden lg:block relative">
                <img src="https://images.pexels.com/photos/19211707/pexels-photo-19211707.jpeg" alt="London skyline" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-tr from-dr-navy/95 to-dr-navy/60" />
                <div className="absolute bottom-12 left-12 right-12 text-white max-w-md">
                    <span className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-white/10 mb-6">
                        <Shield className="w-5 h-5" />
                    </span>
                    <p className="text-xs tracking-[0.32em] uppercase text-dr-blue font-medium">Dynamic Risks</p>
                    <h2 className="font-display text-4xl font-bold mt-4">Admin console</h2>
                    <p className="text-white/70 mt-4">Publish insights, news and manage enquiries in one place.</p>
                </div>
            </div>
            <div className="flex items-center justify-center p-8">
                <form onSubmit={submit} className="w-full max-w-md">
                    <Link to="/" className="text-dr-navy inline-flex items-center gap-2 text-sm mb-8">
                        ← Back to site
                    </Link>
                    <h1 className="font-display text-4xl font-bold text-dr-navy">Sign in</h1>
                    <p className="text-dr-dark/70 mt-2">Access the Dynamic Risks admin console.</p>
                    <div className="mt-8 space-y-6">
                        <div>
                            <label className="text-xs uppercase tracking-[0.24em] text-dr-dark/60 font-medium">Email</label>
                            <input
                                data-testid="login-email"
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="w-full pb-2.5 pt-3 bg-transparent border-0 border-b-2 border-dr-silver focus:border-dr-blue outline-none text-dr-navy"
                            />
                        </div>
                        <div>
                            <label className="text-xs uppercase tracking-[0.24em] text-dr-dark/60 font-medium">Password</label>
                            <input
                                data-testid="login-password"
                                type="password"
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="w-full pb-2.5 pt-3 bg-transparent border-0 border-b-2 border-dr-silver focus:border-dr-blue outline-none text-dr-navy"
                            />
                        </div>
                    </div>
                    <button
                        data-testid="login-submit"
                        disabled={busy}
                        className="mt-8 inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-dr-navy text-white font-semibold hover:bg-dr-blue disabled:opacity-60 transition-colors"
                    >
                        {busy ? "Signing in…" : "Sign in"}
                        <ArrowUpRight className="w-4 h-4" />
                    </button>
                </form>
            </div>
        </div>
    );
}
